import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    onAuthStateChanged, 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword,
    signOut 
} from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                // Check allowed emails if configured
                const allowedEmailsStr = process.env.REACT_APP_ALLOWED_EMAILS;
                if (allowedEmailsStr) {
                    const allowedEmails = allowedEmailsStr.split(',').map(e => e.trim().toLowerCase());
                    if (!allowedEmails.includes(firebaseUser.email.toLowerCase())) {
                        console.warn(`Unauthorized email: ${firebaseUser.email}`);
                        await signOut(auth);
                        setUser(null);
                        setLoading(false);
                        return;
                    }
                }

                // Get the ID token to send to backend
                const token = await firebaseUser.getIdToken();
                setUser({
                    id: firebaseUser.uid,
                    email: firebaseUser.email,
                    username: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                    token,
                    // Status will be fetched from backend if needed
                    status: 'APPROVED',
                });
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const login = async (email, password) => {
        const allowedEmailsStr = process.env.REACT_APP_ALLOWED_EMAILS;
        if (allowedEmailsStr) {
            const allowedEmails = allowedEmailsStr.split(',').map(e => e.trim().toLowerCase());
            if (!allowedEmails.includes(email.toLowerCase())) {
                throw new Error('Your email is not authorized to use this application.');
            }
        }
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return userCredential.user;
    };

    const register = async (email, password) => {
        const allowedEmailsStr = process.env.REACT_APP_ALLOWED_EMAILS;
        if (allowedEmailsStr) {
            const allowedEmails = allowedEmailsStr.split(',').map(e => e.trim().toLowerCase());
            if (!allowedEmails.includes(email.toLowerCase())) {
                throw new Error('Your email is not authorized to register for this application.');
            }
        }
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        return userCredential.user;
    };

    const logout = async () => {
        await signOut(auth);
        setUser(null);
    };

    // Refresh token periodically (Firebase tokens expire after 1 hour)
    useEffect(() => {
        if (!user) return;

        const refreshToken = async () => {
            const currentUser = auth.currentUser;
            if (currentUser) {
                const token = await currentUser.getIdToken(true);
                setUser(prev => prev ? { ...prev, token } : null);
            }
        };

        // Refresh token every 50 minutes
        const interval = setInterval(refreshToken, 50 * 60 * 1000);
        return () => clearInterval(interval);
    }, [user]);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, register, isAuthenticated: !!user }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
